import { captureException, withScope } from "@sentry/react";
import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type { BackgroundImageSource } from "@brickme/project-core/src/image-build/operation.ts";
import type {
	BrickedPictureOutput,
	Palette,
	PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import type {
	BuildWorkerIncomingData,
	BuildWorkerOutgoingData,
} from "./build-worker-data.ts";
import { deserializeError } from "../utils/worker-error.ts";
import { createWorkerSupervisor } from "../utils/worker-supervisor.ts";

type BrickedBuildListener = (result: BrickedPictureOutput) => void;

// One slot per piece of worker state; a later message for a slot supersedes
// the earlier one, which is why set-picture and clear-picture share a slot.
type StateSlot = "sources" | "picture" | "palette" | "backgroundImages";

// A dead worker leaves the editor showing the unbuilt photo until a reload
// (BRICKME-PIX-A-PIC-CREATOR-38). The later delays ride out the network drops
// seen taking down every request on the page at once.
const startRetryDelaysMs = [1_000, 3_000, 10_000, 30_000];

function createBuildWorkerBridge() {
	const listeners: Array<BrickedBuildListener> = [];

	// postMessage structured-clones every (multi-MB) bitmap before the worker
	// can dedup, so skip identity-only changes here. Records are immutable -
	// the id list pins the content.
	let lastPostedBackgroundImageIds: readonly string[] | undefined;

	// Messages posted to a worker that never started are lost, so a replacement
	// worker is sent the latest of each instead.
	const latestState: Partial<Record<StateSlot, BuildWorkerIncomingData>> = {};

	function onMessage(e: MessageEvent) {
		const data = e.data as BuildWorkerOutgoingData;
		switch (data.type) {
			case "bricked-result": {
				for (const listener of listeners) {
					listener(data.output);
				}
				break;
			}
			case "build-error": {
				// The build worker has no Sentry of its own, so a swallowed failure
				// means the build silently never arrives. Surface anything it forwards.
				const error = deserializeError(data.error);
				// eslint-disable-next-line no-console
				console.error(`Build worker error (${data.context})`, error);
				withScope((scope) => {
					scope.setTag("source", "build-worker");
					scope.setExtra("buildWorkerErrorContext", data.context);
					captureException(error);
				});
				break;
			}
			case "ready":
				// Consumed by the supervisor.
				break;
			default:
				console.error("Unknown build message", data);
				throw new Error("Editor got unknown message");
		}
	}

	const supervisor = createWorkerSupervisor({
		source: "build-worker",
		label: "Build worker",
		errorContextExtra: "buildWorkerErrorContext",
		// Note: neither format of worker works locally for me atm. Either in a built preview or
		// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
		createWorker: () =>
			new Worker(new URL("./build-worker.ts", import.meta.url), {
				type: "module",
			}),
		onMessage,
		startRetryDelaysMs,
		onRestart(worker) {
			for (const data of Object.values(latestState)) {
				worker.postMessage(data);
			}
		},
	});

	function post(slot: StateSlot, data: BuildWorkerIncomingData) {
		latestState[slot] = data;
		supervisor.postMessage(data);
	}

	return {
		addEventListener(_: "brickedBuild", listener: BrickedBuildListener) {
			listeners.push(listener);
		},
		removeEventListener(
			eventName: "brickedBuild",
			listener: BrickedBuildListener,
		) {
			const index = listeners.indexOf(listener);
			if (index === -1) {
				console.error(`Listener for ${eventName} not found`, listener);
				return;
			}

			listeners.splice(index, 1);
		},
		setBuildSources(sources: BuildSources | undefined) {
			post("sources", { type: "set-build-sources", sources });
		},
		clearPicture() {
			post("picture", { type: "clear-picture" });
		},
		setPicture(picture: PictureConfiguration) {
			post("picture", { type: "set-picture", picture });
		},
		setPalette(palette: Palette | undefined) {
			post("palette", { type: "set-palette", palette });
		},
		setBackgroundImages(backgroundImages: readonly BackgroundImageSource[]) {
			const ids = backgroundImages.map((b) => b.id);
			if (
				lastPostedBackgroundImageIds !== undefined &&
				ids.length === lastPostedBackgroundImageIds.length &&
				ids.every((id, i) => id === lastPostedBackgroundImageIds?.[i])
			) {
				return;
			}
			lastPostedBackgroundImageIds = ids;
			post("backgroundImages", {
				type: "set-background-images",
				backgroundImages,
			});
		},
		terminate() {
			supervisor.terminate();
		},
	};
}

type BuildWorker = ReturnType<typeof createBuildWorkerBridge>;

export type { BuildWorker };
export { createBuildWorkerBridge };
